<template lang="pug">
  .manageInstallation
    //- Barra de navegación
    van-nav-bar(
      :title="title"
      left-arrow
      @click-left="$emit('close')"
      @click-right="saveInstallation")
      van-icon(name="save" slot="right")

    .main
      van-cell-group

        //- Name input
        van-field.installations__name(
          required
          border=false
          input-align="right"
          name="name"
          v-model="name"
          v-validate="{ required: true, max:15, min:1, regex: nameRegex }"
          :data-vv-as="$t('addUnit.unit.groupName')"
          :label="$t('addUnit.unit.groupName')"
          :class="{'error': errors.has('name') }"
          :placeholder="$t('addUnit.unit.groupNamePlaceholder')")

        //- Units input
        van-cell.installations__units(
          required
          :is-link="!installation"
          :disabled="!installation"
          :title="$t('settings.units')"
          :value="tempUnit"
          @click="showManageUnits")

        //- Timezone input
        van-cell(
          required
          is-link
          :title="$t('addUnit.unit.timeZone')"
          :value="timezone"
          @click="showManageTimezones")

      //- Errores de validación del formulario
      .padding-halve-top.padding-halve-bottom
        .auth__error(v-show="errors.has('name')") {{ errors.first('name') }}

    //- Botones
    .button-bottom-group(v-if="installation")
      .button-bottom.text-danger(@click="confirmDeleteInstallation") {{ $t('addUnit.delete') }}

    //- Popup Selector de Unidades de temperatura
    van-popup.van-popup--full(
      v-model="openManageUnits"
      position="right"
      :overlay="false")

      ManageUnits(
        v-if="openManageUnits"
        :installationUnit="units"
        @setUnit="setUnit"
        @close="hideManageUnits")

    //- Popup Selector de Timezone
    van-popup.van-popup--full(
      v-model="openManageTimezones"
      position="right"
      :overlay="false")

      ManageTimezones(
        v-if="openManageTimezones"
        :timezone="timezone"
        @closeView="hideManageTimezones"
        @timezone="setTimezone")
</template>

<script>
import i18n from '@/locales/i18n';
import { getErrorId } from '@/services/error.service';
import InstallationService from '@/services/installation.service';
import ManageUnits from '@/components/Units/ManageUnits.vue';
import ManageTimezones from '@/components/Units/ManageTimezones.vue';
import { UnitFilter } from '@/filters/Temp.filter';
import { consoleError } from '@/utils/console.utils';
import { CONFIG, REGEX } from '@/app.config';
import { mapGetters } from 'vuex';

export default {
  components: { ManageUnits, ManageTimezones },
  filters: { UnitFilter },

  data() {
    return {
      installation: null,
      units: CONFIG.DEFAULT_UNITS,
      name: '',
      timezone: '',
      latitude: null,
      longitude: null,
      isLoading: false,
      installationError: null,
      nameRegex: REGEX.INSTALLATIONS_NAMES,
      openManageUnits: false,
      openManageTimezones: false,
    };
  },

  props: {
    /**
     * ID de la instalación a la que pertenece el dispositivo
     */
    installationID: {
      type: String,
      default: null,
    },
  },

  computed: {
    ...mapGetters({
      getInstallation: 'unitsModule/getInstallation',
      isDemo: 'globalModule/isDemo',
    }),
    title() {
      return this.installation ? i18n.t('addUnit.unit.editGroup') : i18n.t('addUnit.unit.addGroup');
    },

    /**
     * Devuelve la unidad de temperatura
     */
    tempUnit() {
      return this.$options.filters.UnitFilter(this.units);
    },
  },

  methods: {
    /**
     * Obtiene los datos de una instalación almacenados en el Store
     */
    getInstallationData(installationID) {
      this.installation = this.getInstallation(installationID);
      this.units = this.installation.units;
      this.name = this.installation.name;
      this.timezone = this.installation.timezone;
    },

    /**
     * Salva una instalación
     *
     * Comprueba si los datos del formulario son válidos
     * Comprueba si se trata de una instalación nueva o de una edición
     */
    saveInstallation() {
      this.$validator.validateAll().then(result => {
        if (result) {
          if (this.installationID) {
            this.editInstallation();
          } else {
            this.addInstallation();
          }
        }
      });
    },

    /**
     * Añade una nueva instalación
     */
    async addInstallation() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.installationError = null;

        // Preparo los datos
        const installation = {
          name: this.name,
          timezoneId: this.timezone,
          units: this.units,
        };

        if (CONFIG.SEND_COORD && this.latitude && this.longitude) {
          installation.coords = {
            lat: this.latitude,
            lng: this.longitude,
          };
        }

        // Realizo la petición
        const installationID = await InstallationService.saveInstallation(installation);

        this.$toast.clear();

        // Cierro el componente y devuelvo los datos de la instalación creada
        this.$emit('saveInstallation', { installationID, name: this.name });
      } catch (error) {
        // Obtengo el error
        this.installationError = getErrorId(error);

        this.$toast.clear();

        // Indico que ocurrió un error y cierro el componente
        this.$dialog
          .alert({
            title: i18n.t('manageUnit.fails.title'),
            message: i18n.t('manageUnit.fails.message'),
          })
          .then(() => {
            this.$emit('close');
          });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Edita los datos de una instalación
     */
    async editInstallation() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.installationError = null;

        // Obtengo los datos
        const installation = {
          id: this.installationID,
          name: this.name,
          timezoneId: this.timezone,
        };

        // Realizo la petición
        await InstallationService.updateInstallation(installation);

        this.$toast.clear();
        this.$emit('editInstallation');
      } catch (error) {
        // Obtengo el error
        this.installationError = getErrorId(error);

        this.$toast.clear();
        this.$toast.fail({ mask: true, message: i18n.t('manageUnit.fails.message') });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Confirma si realmente quiere eliminar la instalación
     */
    confirmDeleteInstallation() {
      this.$dialog
        .confirm({
          title: i18n.t('manageUnit.deleteConfirm.title'),
          message: i18n.t('manageUnit.deleteConfirm.message'),
        })
        .then(() => {
          this.deleteInstallation();
        });
    },

    /**
     * Elimina una instalación
     */
    async deleteInstallation() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.installationError = null;

        // Realizo la petición
        await InstallationService.deleteInstallation(this.installationID);

        this.$toast.clear();
        this.$emit('deleteInstallation');
      } catch (error) {
        // Obtengo el error
        this.installationError = getErrorId(error);

        this.$toast.clear();
        this.$toast.fail({
          mask: true,
          message: i18n.t('manageUnit.fails.message'),
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Muestra el modal para seleccionar unidades
     */
    showManageUnits() {
      if (this.installation) return;
      this.openManageUnits = true;
    },

    /**
     * Oculta el modal para seleccionar unidades
     */
    hideManageUnits() {
      this.openManageUnits = false;
    },

    /**
     * Guarda la unidad seleccionada y cierra el modal
     */
    setUnit(units) {
      this.units = units;
      this.hideManageUnits();
    },

    /**
     * Muestra el modal para seleccionar el timezone
     */
    showManageTimezones() {
      if (this.isDemo) return;
      this.openManageTimezones = true;
    },

    /**
     * Oculta el modal para seleccionar el timezone
     */
    hideManageTimezones() {
      this.openManageTimezones = false;
    },

    /**
     * Guarda lel timezone seleccionado y cierra el modal
     */
    setTimezone(timezone) {
      this.timezone = timezone;
      this.hideManageTimezones();
    },
  },

  created() {
    /**
     * Comrpuebo si se trata de una instalación nueva o de editar una instalación
     */
    if (this.installationID) {
      this.getInstallationData(this.installationID);
    } else {
      /**
       * Intento obtener el timezone del teléfono para mostrarlo por defecto
       */
      navigator.globalization.getDatePattern(date => {
        this.timezone = date.iana_timezone;
      });

      /**
       * Si está configurado en la aplicación
       * intento obtener las coordenadas del teléfono para asociarselas a la instalación
       */
      if (CONFIG.SEND_COORD) {
        navigator.geolocation.getCurrentPosition(
          position => {
            console.log(position);
            this.latitude = position.coords.latitude;
            this.longitude = position.coords.longitude;
            this.$dialog.alert({
              title: 'Coordenadas',
              message: position.coords,
            });
          },
          error => {
            consoleError('Error getting position', error);

            this.$dialog
              .alert({
                title: i18n.t('manageUnit.LocationNotAvailable.title'),
                message: i18n.t('manageUnit.LocationNotAvailable.message'),
              })
              .then(() => {
                this.$emit('close', true);
              });
          }
        );
      }
    }
  },
};
</script>
